/**
 * PLANTILLAS DE PARTIDA.
 *
 * El lienzo vacio es donde mas gente abandona: «no se que poner» no lo arregla
 * ninguna herramienta. Una plantilla lo convierte en «cambio el nombre y ya».
 *
 * Cada plantilla se dibuja sobre un lienzo de REFERENCIA y luego se lleva a la
 * zona real con `llevarAZona`, que es la misma funcion que usa «lo mismo pero
 * en sudadera». Se intento generar cada medida como una fraccion de la zona, y
 * no funciona: en una taza de 200 x 85 las alturas relativas al ancho se salen
 * por abajo. Encajar el conjunto entero de una vez sí respeta la composicion.
 */
import {
  IDENTITY_MATRIX,
  hashDeFuente,
  proporcionDeForma,
  trazadoDeForma,
  type Color,
  type Layer,
  type NombreForma,
} from '@besbello/design-core';
import { llevarAZona } from './grupos.js';

/**
 * Un hueco que el cliente rellena ANTES de insertar la plantilla.
 *
 * «Rellena y listo»: al elegir la plantilla de equipo se pregunta el nombre y
 * el dorsal, y las capas salen ya con lo suyo. Sin esto, el cliente insertaba
 * el ejemplo y tenia que ir capa por capa sustituyendo textos.
 */
export interface CampoPlantilla {
  id: string;
  /** Clave i18n de la etiqueta del campo. */
  clave: string;
  porDefecto: string;
}

export interface Plantilla {
  id: string;
  /** Lienzo sobre el que esta dibujada. Se encaja en la zona real al insertarla. */
  referencia: { widthMm: number; heightMm: number };
  /** Clave de i18n del nombre, no el nombre: la interfaz habla tres idiomas. */
  clave: string;
  /** Ocasion para la que sirve, que es como la busca el cliente. */
  categoria: 'grupo' | 'empresa' | 'evento' | 'deporte';
  /** Huecos a rellenar antes de insertar, si los hay. */
  campos?: CampoPlantilla[];
  construir(zona: { widthMm: number; heightMm: number }, tinta: Color, valores?: Record<string, string>): Layer[];
}

const NEGRO: Color = { type: 'rgb', r: 17, g: 17, b: 17 };
const VERSION_CATALOGO = '2026-08';

function texto(input: {
  texto: string;
  familia: string;
  peso?: number;
  cuerpoMm: number;
  x: number;
  y: number;
  color: Color;
  align?: 'left' | 'center' | 'right';
  mayusculas?: boolean;
  arco?: { radiusMm: number; direction: 'up' | 'down' };
}): Layer {
  const peso = input.peso ?? 400;
  return {
    id: crypto.randomUUID(),
    kind: 'text',
    z: 0,
    transform: { ...IDENTITY_MATRIX, e: input.x, f: input.y },
    anchor: 'top-left',
    opacity: 1,
    blend: 'normal',
    locked: false,
    text: input.texto,
    font: {
      family: input.familia,
      version: VERSION_CATALOGO,
      fileHash: hashDeFuente(input.familia, peso, false),
      weight: peso,
      italic: false,
    },
    sizeMm: input.cuerpoMm,
    tracking: 0,
    lineHeightMm: input.cuerpoMm * 1.2,
    direction: 'ltr',
    align: input.align ?? 'center',
    textCase: input.mayusculas ? 'upper' : 'none',
    color: input.color,
    ...(input.arco ? { arc: input.arco } : {}),
  } as Layer;
}

function forma(input: {
  forma: NombreForma;
  anchoMm: number;
  x: number;
  y: number;
  color: Color;
  soloTrazo?: boolean;
}): Layer {
  const alto = input.anchoMm / proporcionDeForma(input.forma);
  return {
    id: crypto.randomUUID(),
    kind: 'shape',
    z: 0,
    transform: { ...IDENTITY_MATRIX, e: input.x, f: input.y },
    anchor: 'top-left',
    opacity: 1,
    blend: 'normal',
    locked: false,
    path: trazadoDeForma(input.forma, input.anchoMm, alto),
    ...(input.soloTrazo ? { stroke: input.color, strokeWidthMm: 1.5 } : { fill: input.color }),
  } as Layer;
}

/**
 * Las plantillas.
 *
 * Los textos van EN CASTELLANO y a proposito no traducidos: son un ejemplo que
 * el cliente va a sustituir por lo suyo, y traducirlos daria a entender que el
 * texto es parte del diseno y no un hueco que rellenar.
 */
export const PLANTILLAS: readonly Plantilla[] = [
  {
    id: 'despedida',
    clave: 'template.farewell',
    categoria: 'grupo',
    referencia: { widthMm: 280, heightMm: 400 },
    construir(zona, tinta) {
      const centro = zona.widthMm / 2;
      return [
        texto({
          texto: 'DESPEDIDA DE',
          familia: 'Oswald',
          peso: 600,
          cuerpoMm: zona.widthMm * 0.055,
          x: centro - zona.widthMm * 0.3,
          y: zona.heightMm * 0.18,
          color: tinta,
          mayusculas: true,
        }),
        texto({
          texto: 'Nombre',
          familia: 'Great Vibes',
          cuerpoMm: zona.widthMm * 0.16,
          x: centro - zona.widthMm * 0.35,
          y: zona.heightMm * 0.28,
          color: tinta,
        }),
        forma({
          forma: 'linea',
          anchoMm: zona.widthMm * 0.5,
          x: centro - zona.widthMm * 0.25,
          y: zona.heightMm * 0.52,
          color: tinta,
          soloTrazo: true,
        }),
        texto({
          texto: 'Chantada · 2026',
          familia: 'Barlow',
          cuerpoMm: zona.widthMm * 0.045,
          x: centro - zona.widthMm * 0.25,
          y: zona.heightMm * 0.56,
          color: tinta,
        }),
      ];
    },
  },
  {
    id: 'equipo',
    clave: 'template.team',
    categoria: 'deporte',
    referencia: { widthMm: 280, heightMm: 400 },
    campos: [
      { id: 'nombre', clave: 'template.field.teamName', porDefecto: 'C.D. Exemplo' },
      { id: 'dorsal', clave: 'template.field.number', porDefecto: '10' },
    ],
    construir(zona, tinta, valores) {
      const centro = zona.widthMm / 2;
      const anchoEscudo = zona.widthMm * 0.38;
      return [
        // El nombre en arco por encima del escudo: es LA forma de un club.
        texto({
          texto: valores?.['nombre'] || 'C.D. Exemplo',
          familia: 'Anton',
          cuerpoMm: zona.widthMm * 0.075,
          x: centro,
          y: zona.heightMm * 0.2,
          color: tinta,
          mayusculas: true,
          arco: { radiusMm: zona.widthMm * 0.45, direction: 'up' },
        }),
        forma({
          forma: 'escudo',
          anchoMm: anchoEscudo,
          x: centro - anchoEscudo / 2,
          y: zona.heightMm * 0.28,
          color: tinta,
          soloTrazo: true,
        }),
        texto({
          texto: valores?.['dorsal'] || '10',
          familia: 'Bebas Neue',
          cuerpoMm: zona.widthMm * 0.14,
          x: centro - zona.widthMm * 0.08,
          y: zona.heightMm * 0.4,
          color: tinta,
        }),
      ];
    },
  },
  {
    id: 'empresa',
    clave: 'template.company',
    categoria: 'empresa',
    referencia: { widthMm: 280, heightMm: 400 },
    construir(zona, tinta) {
      // Pecho izquierdo: es donde va un logotipo de uniforme de trabajo, y
      // ponerlo centrado seria una camiseta de evento, no ropa laboral.
      return [
        texto({
          texto: 'Tu empresa',
          familia: 'Archivo',
          peso: 700,
          cuerpoMm: zona.widthMm * 0.05,
          x: zona.widthMm * 0.08,
          y: zona.heightMm * 0.12,
          color: tinta,
          align: 'left',
        }),
        texto({
          texto: 'servicio · desde 1998',
          familia: 'Barlow',
          cuerpoMm: zona.widthMm * 0.028,
          x: zona.widthMm * 0.08,
          y: zona.heightMm * 0.19,
          color: tinta,
          align: 'left',
        }),
      ];
    },
  },
  {
    id: 'evento',
    clave: 'template.event',
    categoria: 'evento',
    referencia: { widthMm: 280, heightMm: 400 },
    construir(zona, tinta) {
      const centro = zona.widthMm / 2;
      const anchoBanderola = zona.widthMm * 0.7;
      return [
        texto({
          texto: 'Festa\ndo Viño',
          familia: 'Alfa Slab One',
          cuerpoMm: zona.widthMm * 0.12,
          x: centro - zona.widthMm * 0.35,
          y: zona.heightMm * 0.15,
          color: tinta,
        }),
        forma({
          forma: 'banderola',
          anchoMm: anchoBanderola,
          x: centro - anchoBanderola / 2,
          y: zona.heightMm * 0.45,
          color: tinta,
        }),
        texto({
          texto: 'AGOSTO 2026',
          familia: 'Oswald',
          peso: 600,
          cuerpoMm: zona.widthMm * 0.04,
          x: centro - zona.widthMm * 0.2,
          y: zona.heightMm * 0.5,
          color: NEGRO,
          mayusculas: true,
        }),
      ];
    },
  },
  {
    id: 'cumpleanos',
    clave: 'template.birthday',
    categoria: 'evento',
    referencia: { widthMm: 280, heightMm: 400 },
    construir(zona, tinta) {
      const centro = zona.widthMm / 2;
      const anchoEstrella = zona.widthMm * 0.22;
      return [
        forma({
          forma: 'estrella',
          anchoMm: anchoEstrella,
          x: centro - anchoEstrella / 2,
          y: zona.heightMm * 0.12,
          color: tinta,
        }),
        texto({
          texto: 'Xa teño',
          familia: 'Fredoka',
          cuerpoMm: zona.widthMm * 0.06,
          x: centro - zona.widthMm * 0.2,
          y: zona.heightMm * 0.35,
          color: tinta,
        }),
        texto({
          texto: '7',
          familia: 'Titan One',
          cuerpoMm: zona.widthMm * 0.2,
          x: centro - zona.widthMm * 0.08,
          y: zona.heightMm * 0.44,
          color: tinta,
        }),
      ];
    },
  },
  {
    id: 'pena',
    clave: 'template.crew',
    categoria: 'grupo',
    referencia: { widthMm: 280, heightMm: 400 },
    campos: [
      { id: 'nombre', clave: 'template.field.crewName', porDefecto: 'Peña Os de Sempre' },
      { id: 'anio', clave: 'template.field.year', porDefecto: '2026' },
    ],
    construir(zona, tinta, valores) {
      const centro = zona.widthMm / 2;
      return [
        texto({
          texto: valores?.['nombre'] || 'Peña Os de Sempre',
          familia: 'Bungee',
          cuerpoMm: zona.widthMm * 0.08,
          x: centro,
          y: zona.heightMm * 0.18,
          color: tinta,
          mayusculas: true,
          arco: { radiusMm: zona.widthMm * 0.5, direction: 'up' },
        }),
        forma({ forma: 'estrella', anchoMm: zona.widthMm * 0.3, x: centro - zona.widthMm * 0.15, y: zona.heightMm * 0.32, color: tinta }),
        texto({
          texto: valores?.['anio'] || '2026',
          familia: 'Anton',
          cuerpoMm: zona.widthMm * 0.12,
          x: centro - zona.widthMm * 0.14,
          y: zona.heightMm * 0.58,
          color: tinta,
        }),
      ];
    },
  },
  {
    id: 'aniversario',
    clave: 'template.anniversary',
    categoria: 'evento',
    referencia: { widthMm: 280, heightMm: 400 },
    campos: [
      { id: 'anios', clave: 'template.field.years', porDefecto: '50' },
      { id: 'nombre', clave: 'template.field.name', porDefecto: 'Manolo' },
    ],
    construir(zona, tinta, valores) {
      const centro = zona.widthMm / 2;
      return [
        texto({
          texto: valores?.['anios'] || '50',
          familia: 'Alfa Slab One',
          cuerpoMm: zona.widthMm * 0.3,
          x: centro - zona.widthMm * 0.22,
          y: zona.heightMm * 0.16,
          color: tinta,
        }),
        texto({
          texto: 'años de leyenda',
          familia: 'Barlow',
          cuerpoMm: zona.widthMm * 0.055,
          x: centro - zona.widthMm * 0.25,
          y: zona.heightMm * 0.44,
          color: tinta,
          mayusculas: true,
        }),
        texto({
          texto: valores?.['nombre'] || 'Manolo',
          familia: 'Pacifico',
          cuerpoMm: zona.widthMm * 0.075,
          x: centro - zona.widthMm * 0.2,
          y: zona.heightMm * 0.55,
          color: tinta,
        }),
      ];
    },
  },
  {
    id: 'abuelos',
    clave: 'template.grandpa',
    categoria: 'evento',
    referencia: { widthMm: 280, heightMm: 400 },
    campos: [{ id: 'quien', clave: 'template.field.who', porDefecto: 'abuela' }],
    construir(zona, tinta, valores) {
      const centro = zona.widthMm / 2;
      const quien = valores?.['quien'] || 'abuela';
      return [
        texto({
          texto: 'La mejor',
          familia: 'Barlow',
          cuerpoMm: zona.widthMm * 0.06,
          x: centro - zona.widthMm * 0.18,
          y: zona.heightMm * 0.16,
          color: tinta,
          mayusculas: true,
        }),
        texto({
          texto: quien,
          familia: 'Titan One',
          cuerpoMm: zona.widthMm * 0.12,
          x: centro - zona.widthMm * 0.32,
          y: zona.heightMm * 0.26,
          color: tinta,
        }),
        forma({ forma: 'corazon', anchoMm: zona.widthMm * 0.24, x: centro - zona.widthMm * 0.12, y: zona.heightMm * 0.48, color: tinta }),
        texto({
          texto: 'del mundo',
          familia: 'Barlow',
          cuerpoMm: zona.widthMm * 0.06,
          x: centro - zona.widthMm * 0.19,
          y: zona.heightMm * 0.74,
          color: tinta,
          mayusculas: true,
        }),
      ];
    },
  },
  {
    id: 'dorsal-espalda',
    clave: 'template.backNumber',
    categoria: 'deporte',
    referencia: { widthMm: 280, heightMm: 400 },
    campos: [
      { id: 'nombre', clave: 'template.field.playerName', porDefecto: 'GARCIA' },
      { id: 'dorsal', clave: 'template.field.number', porDefecto: '7' },
    ],
    construir(zona, tinta, valores) {
      const centro = zona.widthMm / 2;
      return [
        texto({
          texto: valores?.['nombre'] || 'GARCIA',
          familia: 'Teko',
          peso: 700,
          cuerpoMm: zona.widthMm * 0.11,
          x: centro,
          y: zona.heightMm * 0.1,
          color: tinta,
          mayusculas: true,
          arco: { radiusMm: zona.widthMm * 0.75, direction: 'up' },
        }),
        texto({
          texto: valores?.['dorsal'] || '7',
          familia: 'Anton',
          cuerpoMm: zona.widthMm * 0.42,
          x: centro - zona.widthMm * 0.14,
          y: zona.heightMm * 0.28,
          color: tinta,
        }),
      ];
    },
  },
  {
    id: 'mascota',
    clave: 'template.pet',
    categoria: 'evento',
    referencia: { widthMm: 280, heightMm: 400 },
    campos: [{ id: 'nombre', clave: 'template.field.petName', porDefecto: 'Trasno' }],
    construir(zona, tinta, valores) {
      const centro = zona.widthMm / 2;
      return [
        forma({ forma: 'circulo', anchoMm: zona.widthMm * 0.42, x: centro - zona.widthMm * 0.21, y: zona.heightMm * 0.14, color: tinta, soloTrazo: true }),
        texto({
          texto: valores?.['nombre'] || 'Trasno',
          familia: 'Titan One',
          cuerpoMm: zona.widthMm * 0.09,
          x: centro - zona.widthMm * 0.3,
          y: zona.heightMm * 0.52,
          color: tinta,
        }),
        texto({
          texto: 'el jefe de la casa',
          familia: 'Barlow',
          cuerpoMm: zona.widthMm * 0.05,
          x: centro - zona.widthMm * 0.22,
          y: zona.heightMm * 0.66,
          color: tinta,
        }),
      ];
    },
  },
  {
    id: 'cumple-nino',
    clave: 'template.kidBirthday',
    categoria: 'evento',
    referencia: { widthMm: 280, heightMm: 400 },
    campos: [
      { id: 'nombre', clave: 'template.field.name', porDefecto: 'Noa' },
      { id: 'anios', clave: 'template.field.years', porDefecto: '5' },
    ],
    construir(zona, tinta, valores) {
      const centro = zona.widthMm / 2;
      return [
        texto({
          texto: valores?.['nombre'] || 'Noa',
          familia: 'Titan One',
          cuerpoMm: zona.widthMm * 0.13,
          x: centro - zona.widthMm * 0.28,
          y: zona.heightMm * 0.14,
          color: tinta,
        }),
        texto({
          texto: 'cumple',
          familia: 'Quicksand',
          peso: 700,
          cuerpoMm: zona.widthMm * 0.065,
          x: centro - zona.widthMm * 0.14,
          y: zona.heightMm * 0.36,
          color: tinta,
        }),
        texto({
          texto: valores?.['anios'] || '5',
          familia: 'Alfa Slab One',
          cuerpoMm: zona.widthMm * 0.26,
          x: centro - zona.widthMm * 0.1,
          y: zona.heightMm * 0.46,
          color: tinta,
        }),
        forma({ forma: 'estrella', anchoMm: zona.widthMm * 0.12, x: centro + zona.widthMm * 0.16, y: zona.heightMm * 0.5, color: tinta }),
      ];
    },
  },
  {
    id: 'negocio-local',
    clave: 'template.localShop',
    categoria: 'empresa',
    referencia: { widthMm: 280, heightMm: 400 },
    campos: [
      { id: 'nombre', clave: 'template.field.businessName', porDefecto: 'Panadería A Fornada' },
      { id: 'desde', clave: 'template.field.since', porDefecto: '1987' },
    ],
    construir(zona, tinta, valores) {
      const centro = zona.widthMm / 2;
      return [
        texto({
          texto: valores?.['nombre'] || 'Panadería A Fornada',
          familia: 'Alfa Slab One',
          cuerpoMm: zona.widthMm * 0.06,
          x: centro,
          y: zona.heightMm * 0.2,
          color: tinta,
          arco: { radiusMm: zona.widthMm * 0.55, direction: 'up' },
        }),
        forma({ forma: 'poligono', anchoMm: zona.widthMm * 0.1, x: centro - zona.widthMm * 0.05, y: zona.heightMm * 0.42, color: tinta }),
        texto({
          texto: 'desde ' + (valores?.['desde'] || '1987'),
          familia: 'Archivo Narrow',
          cuerpoMm: zona.widthMm * 0.05,
          x: centro - zona.widthMm * 0.15,
          y: zona.heightMm * 0.56,
          color: tinta,
          mayusculas: true,
        }),
      ];
    },
  },
];

export function buscarPlantilla(id: string): Plantilla | undefined {
  return PLANTILLAS.find((p) => p.id === id);
}

/**
 * La plantilla ya encajada en la zona real.
 *
 * Es la unica forma en que se debe insertar una: `construir` a secas devuelve
 * el dibujo sobre su lienzo de referencia, que casi nunca es el de la prenda.
 */
export function plantillaEnZona(
  plantilla: Plantilla,
  zona: { widthMm: number; heightMm: number },
  tinta: Color,
  valores?: Record<string, string>,
): Layer[] {
  return llevarAZona(plantilla.construir(plantilla.referencia, tinta, valores), plantilla.referencia, zona).capas;
}
